import type { AiAnalyticsEvents } from "./events/ai";
import type { MerchantAnalyticsEvents } from "./events";

interface ObservationScope {
  userId: string;
  shopId: number;
}

type ObservedEvents = Pick<
  AiAnalyticsEvents,
  "brain_funnel_creation_observed" | "strategy_brief_build_completed"
>;

type CaptureOutcome = <Event extends keyof ObservedEvents>(
  event: Event,
  properties: MerchantAnalyticsEvents[Event],
) => void;

const MAX_RECENT_OUTCOMES = 500;

// Shared across chat/editor remounts and realtime reconnects, scoped by viewer.
// These are browser observations; they cannot account for work completed offline.
export function createAiOutcomeAnalytics(capture: CaptureOutcome) {
  const recentOutcomes = new Set<string>();

  const observeOnce = (
    scope: ObservationScope,
    identity: string,
    captureOutcome: () => void,
  ) => {
    const key = JSON.stringify([scope.userId, scope.shopId, identity]);
    if (recentOutcomes.has(key)) return;
    recentOutcomes.add(key);
    if (recentOutcomes.size > MAX_RECENT_OUTCOMES) {
      const oldest = recentOutcomes.values().next().value;
      if (oldest !== undefined) recentOutcomes.delete(oldest);
    }
    captureOutcome();
  };

  return {
    brainFunnelCreated(
      scope: ObservationScope,
      properties: ObservedEvents["brain_funnel_creation_observed"],
    ) {
      observeOnce(
        scope,
        `brain-funnel:${properties.thread_id}:${properties.funnel_id}`,
        () => {
          capture("brain_funnel_creation_observed", properties);
        },
      );
    },
    briefBuildCompleted(
      scope: ObservationScope,
      properties: ObservedEvents["strategy_brief_build_completed"],
    ) {
      observeOnce(
        scope,
        `brief-build:${properties.brief_id}:${properties.funnel_id}`,
        () => {
          capture("strategy_brief_build_completed", properties);
        },
      );
    },
  };
}
